import type {
  CrmChatProviderKey,
  CrmLeadIntegrationChatTab,
  CrmLeadIntegrationsMap,
  CrmLeadProviderChatMeta,
  CrmLeadVkInfo,
} from '@/api/types/crm/crmLeadFunnels.types'
import { CRM_LEAD_PROVIDER_CHAT_META_EMPTY } from '@/api/types/crm/crmLeadFunnels.types'
import { Field, Lead, LeadField } from '@/api/types/crm/crm.types'
import type { VariableLeadFieldSettings } from '@pages/Admin/CRM/components/VariableLeadField/VariableLeadField'
import type { LeadProfileLead } from '@pages/Admin/CRM/components/LeadProfileModal/LeadProfileModal.types'
import {
  flattenLeadProviderChatTabs,
  getLeadProviderChatAccounts,
  zeroLeadProviderChatUnreadInMeta,
} from './leadProviderChatAccounts'

/** Телефон лида (не родителя) — для Wazzup и чата ВК. */
export const LEAD_VK_CHAT_PHONE_FIELD_NAME = 'PHONE'

export const isLeadMainPhoneField = (uniqueName: string): boolean =>
  uniqueName === LEAD_VK_CHAT_PHONE_FIELD_NAME

/** Лид из карточки или строка таблицы воронок — достаточно массива полей. */
export type LeadFieldsSource = Pick<Lead, 'fields'>

type uniqueNameFieldStatic =
  | '_DELETEBUTTON'
  | '_TAKEBUTTON'
  | '_MANAGERFIO'
  | '_STATUS'

export const getLeadField = (
  lead: LeadFieldsSource | undefined,
  uniqueName: uniqueNameFieldStatic | string,
): LeadField | undefined =>
  lead?.fields?.find((field) => field.unique_name === uniqueName)

export const getLeadFieldValue = (
  lead: LeadFieldsSource | undefined,
  uniqueName: uniqueNameFieldStatic | string,
): string | undefined => getLeadField(lead, uniqueName)?.value

/** Свёртка textarea с лида; `undefined` — бэк ещё не отдал / нет поля. */
export const getLeadFieldCollapsed = (
  lead: LeadFieldsSource | undefined,
  uniqueName: uniqueNameFieldStatic | string,
): boolean | undefined => {
  const collapsed = getLeadField(lead, uniqueName)?.is_collapsed
  return typeof collapsed === 'boolean' ? collapsed : undefined
}

/** Дефолт свёртки из settings поля анкеты. Нет ключа → true (как на бэке). */
export const resolveTextareaCollapsedDefault = (
  settings: unknown,
  topLevelCollapsed?: boolean,
): boolean => {
  if (typeof topLevelCollapsed === 'boolean') return topLevelCollapsed

  let parsed: Record<string, unknown> = {}
  if (typeof settings === 'string') {
    try {
      const raw = JSON.parse(settings)
      parsed = raw && typeof raw === 'object' ? raw : {}
    } catch {
      parsed = {}
    }
  } else if (settings && typeof settings === 'object') {
    parsed = settings as Record<string, unknown>
  }

  const collapsed =
    parsed.is_collapsed ?? parsed.textarea_collapsed_by_default

  if (collapsed === true || collapsed === 1 || collapsed === '1') return true
  if (collapsed === false || collapsed === 0 || collapsed === '0') return false
  return true
}

/**
 * Источник чатов CRM-интеграций: `integrations.{provider}` (+ legacy `vk` для VK).
 */
export type LeadVkChatSource = {
  vk?: CrmLeadVkInfo | null
  integrations?: CrmLeadIntegrationsMap | null
}

/** Мета чатов провайдера у лида (`integrations.max` / `vkontakte` / …). */
export const getLeadProviderChatMeta = (
  lead: unknown,
  provider: CrmChatProviderKey,
): CrmLeadProviderChatMeta | null => {
  if (!lead || typeof lead !== 'object') {
    return null
  }
  const source = lead as LeadVkChatSource
  const fromIntegrations = source.integrations?.[provider]
  if (fromIntegrations) {
    return fromIntegrations
  }
  if (provider === 'vkontakte' && source.vk) {
    return source.vk
  }
  return null
}

/** Телефония независима от наличия VK-диалога; legacy оставлен для старых ответов. */
export const getLeadTelephonyEndpoints = (lead: unknown) => {
  if (!lead || typeof lead !== 'object') {
    return []
  }
  const source = lead as LeadVkChatSource
  const endpoints = source.integrations?.telephony?.endpoints
  if (Array.isArray(endpoints)) {
    return endpoints
  }
  const legacyEndpoints =
    source.integrations?.vkontakte?.telephony_integrations ??
    source.vk?.telephony_integrations
  return Array.isArray(legacyEndpoints) ? legacyEndpoints : []
}

/** Вкладки диалогов провайдера (accounts → dialogs; Wazzup: + channels). */
export const getLeadProviderChatTabs = (
  lead: unknown,
  provider: CrmChatProviderKey,
): CrmLeadIntegrationChatTab[] => {
  const meta = getLeadProviderChatMeta(lead, provider)
  return flattenLeadProviderChatTabs(meta, provider)
}

export const leadHasProviderChat = (
  lead: unknown,
  provider: CrmChatProviderKey,
): boolean => {
  const meta = getLeadProviderChatMeta(lead, provider)
  if (meta?.has_chat) {
    return true
  }
  return getLeadProviderChatTabs(lead, provider).length > 0
}

export const getProviderUnreadCount = (
  lead: unknown,
  provider: CrmChatProviderKey,
): number => {
  const meta = getLeadProviderChatMeta(lead, provider)
  if (!meta) {
    return 0
  }

  const topLevel =
    Number.isFinite(Number(meta.unread_count)) && Number(meta.unread_count) > 0
      ? Math.floor(Number(meta.unread_count))
      : 0

  // Wazzup и др.: unread часто только на accounts/channels/dialogs
  const fromAccounts = getLeadProviderChatAccounts(meta).reduce(
    (sum, account) => {
      const accountCount = Number(account.unread_count ?? 0)
      if (Number.isFinite(accountCount) && accountCount > 0) {
        return sum + Math.floor(accountCount)
      }

      if (account.channels?.length) {
        return (
          sum +
          account.channels.reduce((channelSum, channel) => {
            const channelCount = Number(channel.unread_count ?? 0)
            if (Number.isFinite(channelCount) && channelCount > 0) {
              return channelSum + Math.floor(channelCount)
            }
            return (
              channelSum +
              (channel.dialogs ?? []).reduce((dialogSum, dialog) => {
                const dialogCount = Number(dialog.unread_count ?? 0)
                return (
                  dialogSum +
                  (Number.isFinite(dialogCount) && dialogCount > 0
                    ? Math.floor(dialogCount)
                    : 0)
                )
              }, 0)
            )
          }, 0)
        )
      }

      return (
        sum +
        (account.dialogs ?? []).reduce((dialogSum, dialog) => {
          const dialogCount = Number(dialog.unread_count ?? 0)
          return (
            dialogSum +
            (Number.isFinite(dialogCount) && dialogCount > 0
              ? Math.floor(dialogCount)
              : 0)
          )
        }, 0)
      )
    },
    0,
  )

  // Берём max: top-level может отставать от dialogs и наоборот.
  return Math.max(topLevel, fromAccounts > 0 ? fromAccounts : 0)
}

export const getLeadPrimaryProviderConversationId = (
  lead: unknown,
  provider: CrmChatProviderKey,
): number | null => {
  const first = getLeadProviderChatTabs(lead, provider)[0]
  if (!first) {
    return null
  }
  const id = Number(first.conversation_id)
  return Number.isFinite(id) && id > 0 ? id : null
}

/** Мета VK-чатов лида: `integrations.vkontakte`, иначе legacy `vk`. */
export const getLeadVkChatMeta = (
  lead: unknown,
): CrmLeadProviderChatMeta | null => getLeadProviderChatMeta(lead, 'vkontakte')

/** Вкладки диалогов ВК (уже отсортированы бэком по last_interaction_at desc). */
export const getLeadVkChatTabs = (
  lead: unknown,
): CrmLeadIntegrationChatTab[] => getLeadProviderChatTabs(lead, 'vkontakte')

/** Первый (самый свежий) conversation_id или null. */
export const getLeadPrimaryVkConversationId = (
  lead: unknown,
): number | null => getLeadPrimaryProviderConversationId(lead, 'vkontakte')

/**
 * Локальный патч unread: пишем в `integrations.{provider}` (+ legacy `vk` для VK).
 */
export const patchLeadProviderChatMeta = <T extends object>(
  lead: T,
  provider: CrmChatProviderKey,
  patch: Partial<CrmLeadProviderChatMeta>,
): T => {
  const prevMeta =
    getLeadProviderChatMeta(lead, provider) ?? CRM_LEAD_PROVIDER_CHAT_META_EMPTY
  const nextMeta: CrmLeadProviderChatMeta = {
    ...prevMeta,
    ...patch,
    accounts:
      patch.accounts !== undefined
        ? patch.accounts
        : getLeadProviderChatAccounts(prevMeta),
  }
  const prevIntegrations =
    (lead as LeadVkChatSource).integrations ?? ({} as CrmLeadIntegrationsMap)

  const nextLead = {
    ...lead,
    integrations: {
      ...prevIntegrations,
      [provider]: nextMeta,
    },
  }

  if (provider === 'vkontakte') {
    return {
      ...nextLead,
      vk: nextMeta,
    }
  }

  return nextLead
}

/** Mark-read: обнулить unread у диалога(ов) внутри accounts. */
export const patchLeadProviderChatUnreadZero = <T extends object>(
  lead: T,
  provider: CrmChatProviderKey,
  conversationId?: number,
  updatedAt?: string | null,
): T => {
  const prevMeta =
    getLeadProviderChatMeta(lead, provider) ?? CRM_LEAD_PROVIDER_CHAT_META_EMPTY
  const nextMeta = zeroLeadProviderChatUnreadInMeta(prevMeta, conversationId)
  return patchLeadProviderChatMeta(lead, provider, {
    ...nextMeta,
    ...(updatedAt !== undefined ? { updated_at: updatedAt } : {}),
  })
}

export const patchLeadVkChatMeta = <T extends object>(
  lead: T,
  patch: Partial<CrmLeadProviderChatMeta>,
): T => patchLeadProviderChatMeta(lead, 'vkontakte', patch)

/** К лиду привязан VK-аккаунт (legacy vk_user_id / recipient / VK_LINK). */
export const leadHasVkAccountLinked = (lead: unknown): boolean => {
  if (!lead || typeof lead !== 'object') {
    return false
  }

  const meta = getLeadVkChatMeta(lead)
  const vkUserId = meta?.vk_user_id
  if (vkUserId != null) {
    const trimmed = String(vkUserId).trim()
    if (trimmed.length > 0 && trimmed !== '-') {
      return true
    }
  }

  const hasRecipient = getLeadVkChatTabs(lead).some((tab) => {
    const recipient = String(tab.recipient ?? '').trim()
    return recipient.length > 0 && recipient !== '-'
  })
  if (hasRecipient) {
    return true
  }

  if (leadHasVkChat(lead)) {
    return true
  }

  const href = getLeadVkProfileHrefById(lead)
  if (href) {
    return true
  }

  const fromField =
    lead &&
    typeof lead === 'object' &&
    Array.isArray((lead as LeadFieldsSource).fields)
      ? trimVkLink(getLeadFieldValue(lead as LeadFieldsSource, 'VK_LINK'))
      : ''
  return Boolean(fromField)
}

/** Есть хотя бы один VK-диалог у лида (`has_chat` / accounts). */
export const leadHasVkChat = (lead: unknown): boolean =>
  leadHasProviderChat(lead, 'vkontakte')

export const VK_LEAD_CHAT_BUTTON_LABEL = 'Открыть чат ВКонтакте'

export const VK_LEAD_CHAT_UNAVAILABLE_NOT_LINKED_TOOLTIP =
  'К лиду не прикреплен аккаунт ВК'

/** `vk.has_chat === false` — сообщество не может написать первым. */
export const VK_LEAD_CHAT_UNAVAILABLE_NO_DIALOG_TOOLTIP =
  'Сообщество не может начать диалог с лидом, пока лид сам не напишет в сообщество ВКонтакте'

export type VkLeadChatButtonIntegrationState = {
  isLoading: boolean
  isConfigured: boolean
  isActive: boolean
}

export const formatVkUnreadCountBadge = (count: number): string | null => {
  if (!Number.isFinite(count) || count <= 0) {
    return null
  }
  return count > 99 ? '99+' : String(Math.floor(count))
}

/** Alias: общий бейдж unread для VK / MAX / TG. */
export const formatCrmChatUnreadBadge = formatVkUnreadCountBadge

const trimVkLink = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.trim() : ''

/** Значение для поля VK_LINK: `fields.VK_LINK` с бэка, иначе `vk.vk_links.by_id`. */
export const getLeadVkLinkFormValue = (lead: LeadFieldsSource | undefined): string => {
  const fromField = trimVkLink(getLeadFieldValue(lead, 'VK_LINK'))
  if (fromField) {
    return fromField
  }

  return trimVkLink(getLeadVkProfileHrefById(lead))
}

/** get-lead → `integrations.vkontakte.vk_links.by_id` (legacy: `vk.vk_links`). */
export const getLeadVkProfileHrefById = (lead: unknown): string | null => {
  if (!lead || typeof lead !== 'object') {
    return null
  }

  const href = trimVkLink(getLeadVkChatMeta(lead)?.vk_links?.by_id)
  if (!href || href === '-') {
    return null
  }

  return href
}

/** Короткая подпись для UI: `id157270198` или хвост URL. */
export const formatVkProfileHrefLabel = (href: string | null): string => {
  if (!href) {
    return '—'
  }

  const trimmed = href.trim()
  const idMatch = trimmed.match(/vk\.com\/id(\d+)/i)
  if (idMatch?.[1]) {
    return `vk.com/id${idMatch[1]}`
  }

  try {
    const pathname = new URL(trimmed).pathname.replace(/^\//, '')
    return pathname ? `vk.com/${pathname}` : trimmed
  } catch {
    return trimmed
  }
}

/** get-lead / get-leads → сумма unread по VK-диалогам провайдера. */
export const getVkUnreadCount = (lead: unknown): number =>
  getProviderUnreadCount(lead, 'vkontakte')

export const getLeadVkUpdatedAt = (lead: unknown): string | null | undefined => {
  if (!lead || typeof lead !== 'object') {
    return undefined
  }
  const updatedAt = getLeadVkChatMeta(lead)?.updated_at
  return updatedAt == null ? null : String(updatedAt)
}

export const getVkLeadChatButtonState = (
  integration: VkLeadChatButtonIntegrationState,
  lead: unknown,
): { isEnabled: boolean; tooltipTitle: string | null } => {
  if (integration.isLoading) {
    return {
      isEnabled: false,
      tooltipTitle: null,
    }
  }
  if (!integration.isConfigured) {
    return {
      isEnabled: false,
      tooltipTitle: 'Интеграция ВКонтакте не подключена',
    }
  }
  if (!integration.isActive) {
    return {
      isEnabled: false,
      tooltipTitle: 'Интеграция ВКонтакте отключена',
    }
  }
  if (!leadHasVkAccountLinked(lead)) {
    return {
      isEnabled: false,
      tooltipTitle: VK_LEAD_CHAT_UNAVAILABLE_NOT_LINKED_TOOLTIP,
    }
  }
  if (!leadHasVkChat(lead)) {
    return {
      isEnabled: false,
      tooltipTitle: VK_LEAD_CHAT_UNAVAILABLE_NO_DIALOG_TOOLTIP,
    }
  }
  return {
    isEnabled: true,
    tooltipTitle: VK_LEAD_CHAT_BUTTON_LABEL,
  }
}

export const buildLeadPhoneFieldSettings = (options: {
  canInteractWithIntegrations: boolean
  lead: LeadProfileLead
  field: Field
  defaultValue: string
  currentValue: string
  showVkChatButton?: boolean
  onOpenVkChat: (options?: { conversationId?: number }) => void
  onOpenMaxChat?: (options?: { conversationId?: number }) => void
  onOpenTelegramChat?: (options?: { conversationId?: number }) => void
  onOpenWazzupChat?: (options?: { conversationId?: number }) => void
}): VariableLeadFieldSettings => {
  const {
    canInteractWithIntegrations,
    lead,
    field,
    defaultValue,
    currentValue,
    showVkChatButton = isLeadMainPhoneField(field.unique_name),
    onOpenVkChat,
    onOpenMaxChat,
    onOpenTelegramChat,
    onOpenWazzupChat,
  } = options

  const showProviderChats = isLeadMainPhoneField(field.unique_name)
  const vkUnreadCount = getVkUnreadCount(lead)
  const maxUnreadCount = getProviderUnreadCount(lead, 'max')
  const telegramUnreadCount = getProviderUnreadCount(lead, 'telegram')
  const wazzupUnreadCount = getProviderUnreadCount(lead, 'wazzup')

  const withIntegrations =
    canInteractWithIntegrations &&
    !!defaultValue &&
    currentValue === defaultValue && {
      lead: { id: lead.id },
      fieldId: field.id,
      fieldSettings: field.settings,
    }

  return {
    phoneNumber: {
      leadId: lead.id,
      fieldId: field.id,
      fieldSettings: field.settings,
      withIntegrations,
      showVkChatButton,
      showMaxChatButton: showProviderChats,
      showTelegramChatButton: showProviderChats,
      showWazzupChatButton: showProviderChats,
      vkLeadSource: showProviderChats ? lead : undefined,
      onOpenVkChat,
      onOpenMaxChat,
      onOpenTelegramChat,
      onOpenWazzupChat,
      vkUnreadCount: showVkChatButton ? vkUnreadCount : 0,
      maxUnreadCount: showProviderChats ? maxUnreadCount : 0,
      telegramUnreadCount: showProviderChats ? telegramUnreadCount : 0,
      wazzupUnreadCount: showProviderChats ? wazzupUnreadCount : 0,
    },
  }
}

/** Валидация как на бэке: только http/https, схема обязательна. */
export const isValidLeadLinkValue = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) {
    return true
  }

  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export const getClickableUrl = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed || trimmed === '-') {
    return null
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed
  }
  if (/^[\w.-]+\.[a-z]{2,}/i.test(trimmed)) {
    return `https://${trimmed}`
  }
  return null
}
